import React, { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const SECTION_LABELS = {
  // Daily
  start_of_day: '🌅 Start of Day',
  during_day: '☀️ During the Day',
  end_of_day: '🌆 End of Day',
  summary: '📋 End-of-Day Summary',
  // Weekly
  delivery_review: '🚚 Delivery Review',
  delivery_cleanup: '🧹 Delivery Record Clean-Up',
  expense_review: '💰 Expense Review',
  expense_summary: '📊 Weekly Expense Summary',
  cash_review: '💵 Cash Review',
  customer_issues: '👥 Customer & Front Desk Issues',
  packages: '📦 Packages & Incoming Deliveries',
  unresolved_review: '⚠️ Unresolved Items Review',
  data_check: '🖥️ BloansBooks Data Check',
  weekly_close: '✅ Weekly Close',
  weekly_exceptions: '📋 Weekly Exceptions',
  next_week_priorities: '🎯 Priorities for Next Week',
  // Monthly
  delivery_close: '🚚 Monthly Delivery Close',
  delivery_exceptions: '⚠️ Monthly Delivery Exceptions',
  expense_close: '💰 Monthly Expense Close',
  cash_review_monthly: '💵 Monthly Cash Review',
  vat_preparation: '🧾 VAT Preparation',
  vat_review: '🔍 VAT Review & Approval',
  vat_filing: '📤 VAT Filing',
  vat_payment: '💳 VAT Payment',
  vat_completion: '✅ VAT Completion Check',
  aged_items: '⏰ Aged Unresolved Items',
  record_quality: '📋 BloansBooks Record Quality Check',
  month_end_close: '✅ Month-End Close',
  new_month_priorities: '🎯 Priorities for New Month',
  general: '📋 General',
  // Brand Content
  content_creation: '📱 Content Creation',
  planning: '📅 Planning',
  leads: '🎯 Leads & Engagement',
  performance: '📊 Performance Review',
  archive: '🗂️ Archive',
  reporting: '📋 Reporting',
  improvements: '💡 Improvements',
  caption_check: '✍️ Caption Check',
  blog_check: '📝 Blog Post Check',
  pre_shoot: '📷 Pre-Shooting Check',
  photo_quality: '🖼️ Photo Quality Check',
  content_selection: '✅ Content Selection',
  carousel_check: '🎠 Carousel Approval Check',
  // Production Team Lead
  midday: '☀️ Midday Check',
  monthly_report: '📋 Monthly Report',
  weekly_review: '📊 Weekly Review',
  job_order_check: '📋 Job Order Review',
  qc_check: '✅ Final QC Check',
  // Sales Manager
  pipeline_crm: '📊 Sales Pipeline & CRM',
  own_orders: '🛒 Her Own Customers & Orders',
  graphics_printing: '🖨️ Graphics & Printing',
  order_control: '📦 Order Control',
  complaints: '⚠️ Customer Complaints',
  weekly_review: '📊 Weekly Sales Review',
  monthly_review: '📈 Monthly Sales Review',
  // Customer Experience
  pipeline_review: '🔍 Pipeline Review',
  // Customer Experience Officer
  new_enquiries: '📥 New Enquiries',
  follow_up: '🔄 Follow-Up',
  payment_process: '💳 Payment Process',
  artwork_collection: '🎨 Artwork Collection',
  artwork_approval: '✅ Artwork Approval',
  communication_standards: '💬 Communication Standards',
  // Inventory Officer
  receiving: '📦 Receiving Materials',
  material_prep: '✂️ Material Preparation',
  material_issue: '📤 Material Issue',
  production_return: '🔄 Production Return',
  wip_control: '🔧 WIP Control',
  finished_goods: '✅ Finished Goods',
  store_security: '🔒 Store Security'
};

const SECTION_COLORS = {
  start_of_day: '#E8F5EB', during_day: '#FFF3DC', end_of_day: '#E7F5FF',
  summary: '#F2EFE7',
  delivery_review: '#E7F5FF', delivery_cleanup: '#FFF3DC', delivery_close: '#E7F5FF',
  delivery_exceptions: '#FDEAEA',
  expense_review: '#F9F8F5', expense_summary: '#F9F8F5', expense_close: '#F9F8F5',
  cash_review: '#E8F5EB', cash_review_monthly: '#E8F5EB',
  customer_issues: '#FFF3DC', packages: '#F9F8F5',
  unresolved_review: '#FDEAEA', data_check: '#E7F5FF',
  weekly_close: '#E8F5EB', weekly_exceptions: '#F2EFE7', next_week_priorities: '#FFF3DC',
  vat_preparation: '#FFF3DC', vat_review: '#FFF3DC', vat_filing: '#E8F5EB',
  vat_payment: '#E8F5EB', vat_completion: '#E8F5EB',
  aged_items: '#FDEAEA', record_quality: '#F9F8F5',
  month_end_close: '#E8F5EB', new_month_priorities: '#FFF3DC',
  general: '#F9F8F5',
  content_creation: '#E7F5FF', planning: '#FFF3DC', leads: '#E8F5EB',
  performance: '#F9F8F5', archive: '#F2EFE7', reporting: '#F2EFE7',
  improvements: '#FFF3DC', caption_check: '#E7F5FF', blog_check: '#F9F8F5',
  pre_shoot: '#FFF3DC', photo_quality: '#E8F5EB', content_selection: '#F2EFE7',
  carousel_check: '#E7F5FF',
  midday: '#FFF3DC', monthly_report: '#F2EFE7',
  weekly_review: '#F9F8F5', job_order_check: '#E7F5FF', qc_check: '#E8F5EB'
};

const SECTION_BORDER = {
  start_of_day: '#2E6B3A', during_day: '#8B5E0A', end_of_day: '#1864AB',
  summary: '#556B2F',
  delivery_review: '#1864AB', delivery_cleanup: '#8B5E0A', delivery_close: '#1864AB',
  delivery_exceptions: '#8F2E24',
  expense_review: '#556B2F', expense_summary: '#556B2F', expense_close: '#556B2F',
  cash_review: '#2E6B3A', cash_review_monthly: '#2E6B3A',
  customer_issues: '#8B5E0A', packages: '#3B2A21',
  unresolved_review: '#8F2E24', data_check: '#1864AB',
  weekly_close: '#2E6B3A', weekly_exceptions: '#556B2F', next_week_priorities: '#8B5E0A',
  vat_preparation: '#8B5E0A', vat_review: '#8B5E0A', vat_filing: '#2E6B3A',
  vat_payment: '#2E6B3A', vat_completion: '#2E6B3A',
  aged_items: '#8F2E24', record_quality: '#3B2A21',
  month_end_close: '#2E6B3A', new_month_priorities: '#8B5E0A',
  general: '#8A8A8A',
  content_creation: '#1864AB', planning: '#8B5E0A', leads: '#2E6B3A',
  performance: '#556B2F', archive: '#3B2A21', reporting: '#3B2A21',
  improvements: '#8B5E0A', caption_check: '#1864AB', blog_check: '#3B2A21',
  pre_shoot: '#8B5E0A', photo_quality: '#2E6B3A', content_selection: '#556B2F',
  carousel_check: '#1864AB',
  midday: '#8B5E0A', monthly_report: '#3B2A21',
  weekly_review: '#556B2F', job_order_check: '#1864AB', qc_check: '#2E6B3A'
};

const FREQ_LABELS = { 
  daily:'Daily', weekly:'Weekly', monthly:'Monthly',
  on_demand_caption:'Caption Check', on_demand_blog:'Blog Post Check',
  on_demand_shoot:'Pre-Shooting Check', on_demand_photo:'Photo Quality Check',
  on_demand_selection:'Content Selection', on_demand_carousel:'Carousel Approval',
  on_demand_job_order:'Job Order Review', on_demand_qc:'Final QC Check'
};

const ON_DEMAND_TYPES = [
  { key:'on_demand_caption', label:'✍️ Caption Check', role:'brand_content_officer' },
  { key:'on_demand_blog', label:'📝 Blog Post Check', role:'brand_content_officer' },
  { key:'on_demand_shoot', label:'📷 Pre-Shooting Check', role:'brand_content_officer' },
  { key:'on_demand_photo', label:'🖼️ Photo Quality Check', role:'brand_content_officer' },
  { key:'on_demand_selection', label:'✅ Content Selection', role:'brand_content_officer' },
  { key:'on_demand_carousel', label:'🎠 Carousel Approval', role:'brand_content_officer' },
  { key:'on_demand_job_order', label:'📋 Job Order Review', role:'production_team_lead' },
  { key:'on_demand_qc', label:'✅ Final QC Check', role:'production_team_lead' },
];
const ROLE_LABELS = {
  front_desk_officer:'Front Desk Officer', admin:'Admin', sales_manager:'Sales Manager',
  content_sales:'Content & Sales', inventory_officer:'Inventory Officer',
  production_supervisor:'Production Supervisor', production_assistant:'Production Assistant',
  production_staff:'Production Staff', tailor:'Tailor', driver:'Driver',
  expenses_clerk:'Finance & Records', hr_officer:'HR Officer'
};

function lagosNow() { return new Date(Date.now() + 60*60*1000); }
function todayLagos() { return lagosNow().toISOString().split('T')[0]; }

export default function StaffChecklists() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = ['super_admin','admin'].includes(user?.role);

  const canViewTeamPerformance = [
    'super_admin',
    'production_team_lead',
    'sales_manager'
  ].includes(user?.role);
  const [activeTab, setActiveTab] = useState(isAdmin ? 'overview' : 'mine');
  const [frequency, setFrequency] = useState('daily');
  const [date, setDate] = useState(todayLagos());
  const [todayChecklists, setTodayChecklists] = useState([]);
  const [myChecklist, setMyChecklist] = useState(null);
  // MY CHECKLISTS QUEUE
  const [myChecklists, setMyChecklists] = useState([]);

  const [performance, setPerformance] =
    useState(null);
  const [teamPerformance, setTeamPerformance] =
    useState([]);
  const [
    performanceLoading,
    setPerformanceLoading
  ] = useState(false);
  const [
    performanceError,
    setPerformanceError
  ] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [monthlyAuto, setMonthlyAuto] = useState(null);
  const [showOnDemand, setShowOnDemand] = useState(false);

  // SECURE ON-DEMAND CHECKLIST UI
  const [onDemandRuns, setOnDemandRuns] = useState([]);
  const [onDemandLoading, setOnDemandLoading] = useState(false);
  const [startingOnDemand, setStartingOnDemand] = useState('');
  const [onDemandError, setOnDemandError] = useState('');

  // Template management
  const [templates, setTemplates] = useState([]);
  const [templateRole, setTemplateRole] = useState('front_desk_officer');
  const [templateFreq, setTemplateFreq] = useState('daily');
  const [newItem, setNewItem] = useState({ section:'start_of_day', item_text:'', item_type:'checkbox' });
  const [editingItem, setEditingItem] = useState(null);

  const loadToday = useCallback(() => {
    setLoading(true);
    get(`/checklists/today?date=${date}&frequency=${frequency}`)
      .then(d => setTodayChecklists(d.data||[]))
      .finally(() => setLoading(false));
  }, [date, frequency]);

  const loadMine = useCallback(() => {
    setLoading(true);

    Promise.all([
      get(
        `/checklists/mine?date=${encodeURIComponent(date)}&frequency=daily`
      ),
      get(
        `/checklists/mine?date=${encodeURIComponent(date)}&frequency=weekly`
      ),
      get(
        `/checklists/mine?date=${encodeURIComponent(date)}&frequency=monthly`
      ),
      get(
        `/checklists/on-demand?date=${encodeURIComponent(date)}`
      )
    ])
      .then(([
        daily,
        weekly,
        monthly,
        onDemand
      ]) => {
        const scheduled = [
          daily?.data,
          weekly?.data,
          monthly?.data
        ].filter(Boolean);

        const combined = [
          ...scheduled,
          ...(onDemand?.data || [])
        ];

        const unique = Array.from(
          new Map(
            combined.map(record => [
              record.id,
              record
            ])
          ).values()
        );

        setMyChecklists(unique);

        setMyChecklist(current => {
          if (current?.id) {
            const refreshed =
              unique.find(
                record =>
                  record.id === current.id
              );

            if (refreshed) {
              return refreshed;
            }
          }

          const preferred =
            unique.find(
              record =>
                record.frequency ===
                  frequency &&
                !record.is_complete
            ) ||
            unique.find(
              record => !record.is_complete
            ) ||
            unique[0] ||
            null;

          return preferred;
        });
      })
      .catch(error => {
        console.error(
          'My Checklists queue failed:',
          error
        );
      })
      .finally(() =>
        setLoading(false)
      );
  }, [date, frequency]);

  const loadOnDemand = useCallback(() => {
    setOnDemandLoading(true);
    setOnDemandError('');

    get(
      `/checklists/on-demand?date=${encodeURIComponent(date)}`
    )
      .then(d =>
        setOnDemandRuns(d.data || [])
      )
      .catch(error => {
        setOnDemandRuns([]);
        setOnDemandError(
          error.message ||
          'On-demand checklists could not be loaded'
        );
      })
      .finally(() =>
        setOnDemandLoading(false)
      );
  }, [date]);

  const loadPerformance = useCallback(() => {
    setPerformanceLoading(true);
    setPerformanceError('');

    const requests = [
      get('/checklists/performance?scope=self')
    ];

    if (canViewTeamPerformance) {
      requests.push(
        get('/checklists/performance?scope=team')
      );
    }

    Promise.all(requests)
      .then(results => {
        setPerformance(
          results[0]?.data || null
        );

        setTeamPerformance(
          canViewTeamPerformance
            ? (results[1]?.data || [])
            : []
        );
      })
      .catch(error => {
        setPerformance(null);
        setTeamPerformance([]);
        setPerformanceError(
          error.message ||
          'Checklist performance could not be loaded'
        );
      })
      .finally(() =>
        setPerformanceLoading(false)
      );
  }, [canViewTeamPerformance]);


  const loadTemplates = useCallback(() => {
    get(`/checklists/templates?role=${templateRole}&frequency=${templateFreq}`)
      .then(d => setTemplates(d.data||[]));
  }, [templateRole, templateFreq]);

  useEffect(() => {
    if (activeTab === 'overview') { if (isAdmin) loadToday(); else loadMine(); }
    if (activeTab === 'mine') {
      loadMine();
      if (frequency === 'monthly') {
        const [y, m] = date.split('-');
        get(`/checklists/monthly-auto/${y}/${m}`)
          .then(d => setMonthlyAuto(d))
          .catch(() => {});
      }
    }
    if (activeTab === 'on_demand') loadOnDemand();
    if (activeTab === 'templates') loadTemplates();
  }, [activeTab, date, frequency, templateRole, templateFreq]);

  useEffect(() => {
    loadPerformance();
  }, [loadPerformance]);


  // Group items by section
  const groupBySection = (items) => {
    const groups = {};
    (items||[]).forEach(item => {
      const s = item.section || 'general';
      if (!groups[s]) groups[s] = [];
      groups[s].push(item);
    });
    return groups;
  };

  // Save checklist items
  const saveItems = async (checklist, items, summaryData) => {
    setSaving(true);
    try {
      const response =
        await put(
          `/checklists/${checklist.id}/items`,
          {
            items,
            summary_data: summaryData
          }
        );

      const savedRecord =
        response.data || {
          ...checklist,
          items,
          summary_data: summaryData
        };

      setMyChecklist(savedRecord);

      setMyChecklists(current =>
        current.map(record =>
          record.id === savedRecord.id
            ? savedRecord
            : record
        )
      );
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  // Submit checklist
  const submitChecklist = async () => {
    if (!myChecklist) return;
    if (!window.confirm('Submit this checklist with only the work you actually did checked? Unchecked items will remain incomplete. You cannot edit it after submitting.')) return;
    setSaving(true);
    try {
      const response =
        await put(
          `/checklists/${myChecklist.id}/complete`,
          {
            items: myChecklist.items,
            summary_data:
              myChecklist.summary_data || {}
          }
        );

      const completedRecord =
        response.data || {
          ...myChecklist,
          is_complete: true
        };

      setMyChecklist(completedRecord);

      setMyChecklists(current =>
        current.map(record =>
          record.id === completedRecord.id
            ? completedRecord
            : record
        )
      );

      if (
        String(myChecklist.frequency || '')
          .startsWith('on_demand_')
      ) {
        loadOnDemand();
      }

      alert('Checklist submitted successfully!');
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const startOnDemandChecklist = async (
    checklistType
  ) => {
    if (startingOnDemand) return;

    if (
      !window.confirm(
        `Start a new ${checklistType.label}?`
      )
    ) {
      return;
    }

    setStartingOnDemand(checklistType.key);
    setOnDemandError('');

    try {
      const payload = {
        frequency: checklistType.key
      };

      if (isAdmin) {
        payload.role = checklistType.role;
      }

      const response =
        await post(
          '/checklists/start-on-demand',
          payload
        );

      const created = response.data;

      if (!created?.id) {
        throw new Error(
          'The checklist was created but its record was not returned'
        );
      }

      setDate(
        String(created.date || todayLagos())
          .slice(0, 10)
      );
      setFrequency(created.frequency);

      setMyChecklists(current => {
        if (
          current.some(
            record => record.id === created.id
          )
        ) {
          return current;
        }

        return [
          ...current,
          created
        ];
      });

      setMyChecklist(current =>
        current || created
      );

      setActiveTab('mine');

      await loadOnDemand();
    } catch (error) {
      setOnDemandError(
        error.message ||
        'The checklist could not be started'
      );
    } finally {
      setStartingOnDemand('');
    }
  };

  const openOnDemandChecklist = record => {
    setDate(
      String(record.date || date).slice(0, 10)
    );
    setFrequency(record.frequency);
    setMyChecklist(record);
    setActiveTab('mine');
  };

  const tabs = [
    ...(isAdmin ? [{ key:'overview', label:'Overview' }] : []),
    { key:'mine', label:'My Checklists' },
    { key:'performance', label:'My Performance' },
    ...(isAdmin ? [{ key:'templates', label:'Manage Templates' }] : []),
    ...(ON_DEMAND_TYPES.some(t=>t.role===user?.role||isAdmin) ? [{ key:'on_demand', label:'On-Demand Checklists' }] : [])
  ];

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Staff Checklists</h2>
          <p className="text-muted text-sm">Role-based daily, weekly and monthly checklists</p>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <select className="form-control" style={{fontSize:12,padding:'6px 10px'}}
            value={frequency} onChange={e=>{
              setFrequency(e.target.value);
              setMyChecklist(null);
              setMonthlyAuto(null);
            }}>
            {Object.entries(FREQ_LABELS).map(([k,v])=>(
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <input type="date" className="form-control" style={{fontSize:12,padding:'6px 10px'}}
            value={date} onChange={e=>{
              setDate(e.target.value);
              setMyChecklist(null);
              setMyChecklists([]);
              setMonthlyAuto(null);
            }} />
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar mb-lg">
        {tabs.map(t=>(
          <button key={t.key} className={'tab-btn'+(activeTab===t.key?' active':'')}
            onClick={()=>setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* CHECKLIST PERFORMANCE DASHBOARD */}
      {activeTab === 'performance' && (
        <div>
          {performanceLoading ? (
            <div className="spinner"/>
          ) : performanceError ? (
            <div
              style={{
                background:'#FDECEC',
                border:'1px solid #E9B9B4',
                borderRadius:10,
                padding:16,
                color:'#8F2E24'
              }}
            >
              <div style={{
                fontWeight:800,
                marginBottom:6
              }}>
                Performance could not be loaded
              </div>

              <div style={{
                fontSize:12,
                marginBottom:12
              }}>
                {performanceError}
              </div>

              <button
                className="btn btn-outline btn-sm"
                onClick={loadPerformance}
              >
                Try Again
              </button>
            </div>
          ) : performance ? (
            <div>
              <div
                style={{
                  background:
                    'linear-gradient(135deg,#3B2A21,#556B2F)',
                  borderRadius:14,
                  padding:22,
                  color:'#F2EFE7',
                  marginBottom:18
                }}
              >
                <div style={{
                  display:'flex',
                  justifyContent:'space-between',
                  alignItems:'flex-start',
                  gap:18,
                  flexWrap:'wrap'
                }}>
                  <div style={{
                    flex:'1 1 260px'
                  }}>
                    <div style={{
                      fontSize:11,
                      textTransform:'uppercase',
                      letterSpacing:1,
                      color:'rgba(242,239,231,.7)',
                      marginBottom:6
                    }}>
                      30-Day Checklist Compliance
                    </div>

                    <div style={{
                      display:'flex',
                      alignItems:'baseline',
                      gap:8,
                      marginBottom:7
                    }}>
                      <div style={{
                        fontSize:42,
                        lineHeight:1,
                        fontWeight:900
                      }}>
                        {performance.baseline_ready
                          ? `${performance.thirty_day.percentage ?? 0}%`
                          : '—'}
                      </div>

                      {!performance.baseline_ready && (
                        <div style={{
                          fontSize:12,
                          color:'#DCCB9A',
                          fontWeight:700
                        }}>
                          Building baseline
                        </div>
                      )}
                    </div>

                    <div style={{
                      fontSize:15,
                      fontWeight:800,
                      color:'#DCCB9A',
                      marginBottom:5
                    }}>
                      {performance.encouragement?.label}
                    </div>

                    <div style={{
                      fontSize:12,
                      lineHeight:1.6,
                      color:'rgba(242,239,231,.82)',
                      maxWidth:580
                    }}>
                      {performance.encouragement?.message}
                    </div>
                  </div>

                  <div style={{
                    minWidth:190,
                    background:'rgba(255,255,255,.1)',
                    borderRadius:10,
                    padding:14
                  }}>
                    <div style={{
                      fontSize:11,
                      color:'rgba(242,239,231,.65)',
                      marginBottom:5
                    }}>
                      Submitted
                    </div>

                    <div style={{
                      fontSize:22,
                      fontWeight:900,
                      marginBottom:10
                    }}>
                      {performance.thirty_day.completed}
                      {' / '}
                      {performance.thirty_day.assigned}
                    </div>

                    <div style={{
                      height:7,
                      borderRadius:20,
                      overflow:'hidden',
                      background:'rgba(255,255,255,.15)'
                    }}>
                      <div style={{
                        height:'100%',
                        width:
                          `${performance.thirty_day.percentage || 0}%`,
                        maxWidth:'100%',
                        background:'#A8893D',
                        borderRadius:20
                      }}/>
                    </div>
                  </div>
                </div>

                {!performance.baseline_ready && (
                  <div style={{
                    marginTop:16,
                    paddingTop:13,
                    borderTop:
                      '1px solid rgba(255,255,255,.15)',
                    fontSize:11,
                    color:'rgba(242,239,231,.68)'
                  }}>
                    A percentage rating appears after at
                    least 5 scheduled checklist assignments.
                    Your current progress is still recorded.
                  </div>
                )}
              </div>

              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(auto-fit,minmax(180px,1fr))',
                gap:12,
                marginBottom:22
              }}>
                <div style={{
                  background:'#fff',
                  border:'1px solid #E8E0D4',
                  borderRadius:10,
                  padding:16
                }}>
                  <div style={{
                    fontSize:11,
                    color:'#8A8A8A',
                    marginBottom:7
                  }}>
                    Last 7 Days
                  </div>

                  <div style={{
                    fontSize:25,
                    fontWeight:900,
                    color:'#3B2A21'
                  }}>
                    {performance.seven_day.completed}
                    {' / '}
                    {performance.seven_day.assigned}
                  </div>

                  <div style={{
                    fontSize:11,
                    color:'#556B2F',
                    marginTop:5
                  }}>
                    Scheduled checklists submitted
                  </div>
                </div>

                <div style={{
                  background:'#fff',
                  border:'1px solid #E8E0D4',
                  borderRadius:10,
                  padding:16
                }}>
                  <div style={{
                    fontSize:11,
                    color:'#8A8A8A',
                    marginBottom:7
                  }}>
                    Submitted On Time
                  </div>

                  <div style={{
                    fontSize:25,
                    fontWeight:900,
                    color:'#3B2A21'
                  }}>
                    {performance.thirty_day.on_time_percentage
                      === null
                      ? '—'
                      : `${performance.thirty_day.on_time_percentage}%`}
                  </div>

                  <div style={{
                    fontSize:11,
                    color:'#556B2F',
                    marginTop:5
                  }}>
                    Of submitted scheduled checklists
                  </div>
                </div>

                <div style={{
                  background:'#fff',
                  border:'1px solid #E8E0D4',
                  borderRadius:10,
                  padding:16
                }}>
                  <div style={{
                    fontSize:11,
                    color:'#8A8A8A',
                    marginBottom:7
                  }}>
                    Today
                  </div>

                  <div style={{
                    fontSize:25,
                    fontWeight:900,
                    color:'#3B2A21'
                  }}>
                    {performance.today.completed}
                    {' / '}
                    {performance.today.assigned}
                  </div>

                  <div style={{
                    fontSize:11,
                    color:'#556B2F',
                    marginTop:5
                  }}>
                    Live progress — not yet scored
                  </div>
                </div>

                <div style={{
                  background:'#fff',
                  border:'1px solid #E8E0D4',
                  borderRadius:10,
                  padding:16
                }}>
                  <div style={{
                    fontSize:11,
                    color:'#8A8A8A',
                    marginBottom:7
                  }}>
                    On-Demand Activity
                  </div>

                  <div style={{
                    fontSize:25,
                    fontWeight:900,
                    color:'#3B2A21'
                  }}>
                    {performance.on_demand.completed}
                    {' / '}
                    {performance.on_demand.started}
                  </div>

                  <div style={{
                    fontSize:11,
                    color:'#556B2F',
                    marginTop:5
                  }}>
                    Shown separately from your score
                  </div>
                </div>
              </div>

              <div style={{
                background:'#F2EFE7',
                borderRadius:10,
                padding:14,
                fontSize:11,
                color:'#66564B',
                lineHeight:1.6,
                marginBottom:22
              }}>
                Your compliance score measures submitted
                daily, weekly and monthly checklists, not the
                number of tasks performed. Unchecked items
                remain incomplete. Today is counted only after the business
                date closes. On-demand checklists never
                reduce this percentage.
              </div>

              {canViewTeamPerformance && (
                <div>
                  <div style={{
                    display:'flex',
                    justifyContent:'space-between',
                    alignItems:'center',
                    gap:12,
                    marginBottom:12
                  }}>
                    <div>
                      <div style={{
                        fontSize:15,
                        fontWeight:900,
                        color:'#3B2A21'
                      }}>
                        Team Checklist Progress
                      </div>

                      <div style={{
                        fontSize:11,
                        color:'#8A8A8A',
                        marginTop:3
                      }}>
                        Direct reports only. Super Admin
                        can view all active staff.
                      </div>
                    </div>

                    <button
                      className="btn btn-outline btn-sm"
                      onClick={loadPerformance}
                    >
                      Refresh
                    </button>
                  </div>

                  {teamPerformance.length === 0 ? (
                    <div className="empty-state">
                      <h3>No team performance records</h3>
                      <p>
                        No visible employee currently has
                        checklist performance data.
                      </p>
                    </div>
                  ) : (
                    <div style={{
                      display:'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill,minmax(250px,1fr))',
                      gap:12
                    }}>
                      {teamPerformance.map(record => (
                        <div
                          key={record.user_id}
                          style={{
                            background:'#fff',
                            border:'1px solid #E8E0D4',
                            borderRadius:10,
                            padding:16
                          }}
                        >
                          <div style={{
                            display:'flex',
                            justifyContent:
                              'space-between',
                            gap:10,
                            marginBottom:10
                          }}>
                            <div>
                              <div style={{
                                fontSize:13,
                                fontWeight:800,
                                color:'#3B2A21'
                              }}>
                                {record.staff_name}
                              </div>

                              <div style={{
                                fontSize:10,
                                color:'#8A8A8A',
                                textTransform:'capitalize',
                                marginTop:3
                              }}>
                                {(record.staff_role || '')
                                  .replace(/_/g, ' ')}
                              </div>
                            </div>

                            <div style={{
                              fontSize:22,
                              fontWeight:900,
                              color:
                                record.baseline_ready
                                  ? '#556B2F'
                                  : '#A8893D'
                            }}>
                              {record.baseline_ready
                                ? `${record.thirty_day.percentage ?? 0}%`
                                : '—'}
                            </div>
                          </div>

                          <div style={{
                            fontSize:11,
                            fontWeight:800,
                            color:'#556B2F',
                            marginBottom:8
                          }}>
                            {record.encouragement?.label}
                          </div>

                          <div style={{
                            background:'#F2EFE7',
                            borderRadius:20,
                            height:6,
                            overflow:'hidden',
                            marginBottom:8
                          }}>
                            <div style={{
                              height:'100%',
                              width:
                                `${record.thirty_day.percentage || 0}%`,
                              maxWidth:'100%',
                              background:'#A8893D'
                            }}/>
                          </div>

                          <div style={{
                            display:'flex',
                            justifyContent:
                              'space-between',
                            gap:10,
                            fontSize:10,
                            color:'#75675C'
                          }}>
                            <span>
                              30 days:{' '}
                              {record.thirty_day.completed}
                              /
                              {record.thirty_day.assigned}
                            </span>

                            <span>
                              Today:{' '}
                              {record.today.completed}
                              /
                              {record.today.assigned}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <h3>No performance record yet</h3>
              <p>
                Performance will appear after scheduled
                checklists have been assigned.
              </p>
            </div>
          )}
        </div>
      )}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div>
          {loading ? <div className="spinner"/> : todayChecklists.length === 0 ? (
            <div className="empty-state">
              <h3>No checklists for this date</h3>
              <p>Checklists are auto-assigned Monday–Friday at 6am</p>
              {isAdmin && (
                <button className="btn btn-primary" onClick={async()=>{
                  try {
                    await post('/checklists/auto-assign', { frequency, date });
                    loadToday();
                    alert('Checklists assigned successfully');
                  } catch(e) { alert(e.message); }
                }}>Assign Now</button>
              )}
            </div>
          ) : (
            <div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12,marginBottom:20}}>
                {todayChecklists.map(c => {
                  const items = c.items || [];
                  const checkboxItems = items.filter(i=>i.item_type==='checkbox');
                  const checked = checkboxItems.filter(i=>i.checked).length;
                  const total = checkboxItems.length;
                  const pct = total > 0 ? Math.round(checked/total*100) : 0;
                  return (
                    <div key={c.id} style={{background:'#fff',border:'1px solid #E8E0D4',
                      borderRadius:10,padding:16}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                        <div style={{fontWeight:700,fontSize:13,color:'#3B2A21'}}>{c.staff_name}</div>
                        <span style={{fontSize:10,padding:'2px 8px',borderRadius:20,fontWeight:700,
                          background:c.is_complete?'#E8F5EB':'#FFF3DC',
                          color:c.is_complete?'#2E6B3A':'#8B5E0A'}}>
                          {c.is_complete?'✅ Submitted':'⏳ In Progress'}
                        </span>
                      </div>
                      <div style={{fontSize:11,color:'#8A8A8A',marginBottom:10,textTransform:'capitalize'}}>
                        {(c.staff_role||'').replace(/_/g,' ')} · {FREQ_LABELS[c.frequency]||c.frequency}
                      </div>
                      <div style={{background:'#F2EFE7',borderRadius:6,overflow:'hidden',height:6,marginBottom:6}}>
                        <div style={{height:6,background:'#556B2F',width:pct+'%',transition:'width .3s'}}/>
                      </div>
                      <div style={{fontSize:11,color:'#8A8A8A'}}>{checked}/{total} items completed ({pct}%)</div>
                      {c.is_complete && c.completed_at && (
                        <div style={{fontSize:11,color:'#2E6B3A',marginTop:4}}>
                          Submitted {new Date(c.completed_at).toLocaleTimeString('en-NG',{hour:'2-digit',minute:'2-digit'})}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MY CHECKLIST TAB */}
      {activeTab === 'mine' && (
        <div>
          {/* MY CHECKLISTS QUEUE */}
          {myChecklists.length > 0 && (
            <div style={{
              marginBottom:22
            }}>
              <div style={{
                fontWeight:800,
                fontSize:14,
                color:'#3B2A21',
                marginBottom:4
              }}>
                My Checklists for {date}
              </div>

              <div style={{
                color:'#8A8A8A',
                fontSize:11,
                marginBottom:12
              }}>
                Select a checklist to open it. Starting
                another checklist adds it to this list.
              </div>

              <div style={{
                display:'grid',
                gridTemplateColumns:
                  'repeat(auto-fill,minmax(220px,1fr))',
                gap:10
              }}>
                {myChecklists.map(record => {
                  const items = record.items || [];

                  const checkboxItems =
                    items.filter(
                      item =>
                        item.item_type ===
                          'checkbox'
                    );

                  const checked =
                    checkboxItems.filter(
                      item => item.checked
                    ).length;

                  const selected =
                    myChecklist?.id === record.id;

                  const label =
                    FREQ_LABELS[
                      record.frequency
                    ] || record.frequency;

                  const runNumber =
                    String(
                      record.frequency || ''
                    ).startsWith(
                      'on_demand_'
                    )
                      ? ` #${record.seq || 1}`
                      : '';

                  return (
                    <button
                      type="button"
                      key={record.id}
                      onClick={() => {
                        setFrequency(
                          record.frequency
                        );
                        setMyChecklist(record);
                      }}
                      style={{
                        textAlign:'left',
                        background:
                          selected
                            ? '#F2EFE7'
                            : '#fff',
                        border:
                          selected
                            ? '2px solid #556B2F'
                            : '1px solid #E8E0D4',
                        borderRadius:10,
                        padding:'12px 14px',
                        cursor:'pointer'
                      }}
                    >
                      <div style={{
                        display:'flex',
                        justifyContent:
                          'space-between',
                        alignItems:'flex-start',
                        gap:8
                      }}>
                        <div style={{
                          fontWeight:800,
                          fontSize:12,
                          color:'#3B2A21'
                        }}>
                          {label}{runNumber}
                        </div>

                        <span style={{
                          flexShrink:0,
                          fontSize:9,
                          fontWeight:700,
                          borderRadius:20,
                          padding:'3px 7px',
                          background:
                            record.is_complete
                              ? '#E8F5EB'
                              : '#FFF3DC',
                          color:
                            record.is_complete
                              ? '#2E6B3A'
                              : '#8B5E0A'
                        }}>
                          {record.is_complete
                            ? 'Submitted'
                            : selected
                              ? 'Open'
                              : 'Continue'}
                        </span>
                      </div>

                      <div style={{
                        fontSize:10,
                        color:'#8A8A8A',
                        marginTop:7
                      }}>
                        {checked}/
                        {checkboxItems.length}
                        {' '}checkboxes completed
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!myChecklist ? (
            <div className="empty-state">
              <h3>No checklist assigned</h3>
              <p>Your {frequency} checklist for {date} has not been assigned yet.</p>
            </div>
          ) : (
            <div style={{maxWidth:720}}>
              {myChecklist.is_complete && (
                <div style={{background:'#E8F5EB',border:'1px solid #C3E6CB',borderRadius:8,
                  padding:'10px 16px',marginBottom:16,fontSize:13,color:'#2E6B3A',fontWeight:600}}>
                  ✅ Submitted on {myChecklist.completed_at ? new Date(myChecklist.completed_at)
                    .toLocaleString('en-NG',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : 'today'}
                </div>
              )}

              {frequency === 'monthly' && monthlyAuto && (
                <div style={{background:'#F2EFE7',border:'1px solid #E8E0D4',borderRadius:10,padding:16,marginBottom:20}}>
                  <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:12,textTransform:'uppercase',letterSpacing:'.06em'}}>
                    📊 Auto-Populated Figures
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10}}>
                    {[
                      ['Total Expenses',`₦${Number(monthlyAuto.expenses?.total_amount||0).toLocaleString('en-NG')}`,'#556B2F'],
                      ['Cash Expenses',`₦${Number(monthlyAuto.expenses?.cash_amount||0).toLocaleString('en-NG')}`,'#3B2A21'],
                      ['Transfer Expenses',`₦${Number(monthlyAuto.expenses?.transfer_amount||0).toLocaleString('en-NG')}`,'#3B2A21'],
                      ['Pending Expenses',monthlyAuto.expenses?.pending_count||0,'#8B5E0A'],
                      ['Rejected Expenses',monthlyAuto.expenses?.rejected_count||0,'#8F2E24'],
                      ['Expenses Missing Evidence',monthlyAuto.expenses?.missing_evidence||0,'#8F2E24'],
                      ['Total Deliveries',monthlyAuto.deliveries?.total||0,'#1864AB'],
                      ['Outstanding Deliveries',monthlyAuto.deliveries?.outstanding||0,'#8F2E24'],
                      ['Outstanding Over 7 Days',monthlyAuto.deliveries?.outstanding_over_7_days||0,'#8F2E24'],
                      ['Expected Cash Balance',`₦${Number(monthlyAuto.expected_cash||0).toLocaleString('en-NG')}`,'#2E6B3A'],
                    ].map(([label,value,color])=>(
                      <div key={label} style={{background:'#fff',borderRadius:8,padding:'10px 14px',border:'1px solid #E8E0D4'}}>
                        <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>{label}</div>
                        <div style={{fontSize:16,fontWeight:900,color}}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Object.entries(groupBySection(myChecklist.items)).map(([section, items]) => (
                <div key={section} style={{marginBottom:20,borderRadius:10,overflow:'hidden',
                  border:`1px solid ${SECTION_BORDER[section]||'#E8E0D4'}`}}>
                  <div style={{background:SECTION_BORDER[section]||'#556B2F',padding:'10px 16px'}}>
                    <div style={{fontWeight:700,fontSize:13,color:'#fff'}}>
                      {SECTION_LABELS[section]||section}
                    </div>
                  </div>
                  <div style={{background:SECTION_COLORS[section]||'#F9F8F5',padding:16}}>
                    {items.map((item, idx) => {
                      const globalIdx = myChecklist.items.indexOf(item);
                      if (item.item_type === 'text_input') {
                        return (
                          <div key={idx} style={{marginBottom:14}}>
                            <label style={{display:'block',fontSize:12,fontWeight:700,
                              color:'#3B2A21',marginBottom:5}}>{item.text}</label>
                            <textarea
                              disabled={myChecklist.is_complete}
                              value={item.value||''}
                              onChange={e=>{
                                const newItems = [...myChecklist.items];
                                newItems[globalIdx] = {...item, value: e.target.value};
                                setMyChecklist({...myChecklist, items: newItems});
                              }}
                              onBlur={()=>saveItems(myChecklist, myChecklist.items, myChecklist.summary_data)}
                              style={{width:'100%',padding:'8px 12px',border:'1px solid #E8E0D4',
                                borderRadius:6,fontSize:13,minHeight:60,resize:'vertical',
                                background:myChecklist.is_complete?'#F9F8F5':'#fff',
                                boxSizing:'border-box'}}
                              placeholder="Enter your response..." />
                          </div>
                        );
                      }
                      return (
                        <label key={idx} style={{display:'flex',alignItems:'flex-start',gap:10,
                          marginBottom:10,cursor:myChecklist.is_complete?'default':'pointer',
                          padding:'8px 10px',borderRadius:6,
                          background:item.checked?'rgba(85,107,47,.08)':'transparent'}}>
                          <input type="checkbox"
                            disabled={myChecklist.is_complete}
                            checked={item.checked||false}
                            onChange={e=>{
                              const newItems = [...myChecklist.items];
                              newItems[globalIdx] = {...item, checked: e.target.checked};
                              setMyChecklist({...myChecklist, items: newItems});
                              saveItems(myChecklist, newItems, myChecklist.summary_data);
                            }}
                            style={{accentColor:'#556B2F',width:16,height:16,flexShrink:0,marginTop:2}} />
                          <span style={{fontSize:13,color:'#3B2A21',
                            textDecoration:item.checked?'line-through':'none',
                            opacity:item.checked?0.6:1,lineHeight:1.5}}>
                            {item.text}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}

              {!myChecklist.is_complete && (
                <p style={{fontSize:13,color:'#66564B',lineHeight:1.6}}>
                  Check only the work you actually completed. You can submit with
                  unchecked items; they will remain recorded as incomplete.
                  Submitted checklists cannot be edited.
                </p>
              )}
              {!myChecklist.is_complete && (
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button className="btn btn-outline" disabled={saving}
                    onClick={()=>saveItems(myChecklist, myChecklist.items, myChecklist.summary_data)}>
                    {saving?'Saving...':'💾 Save Progress'}
                  </button>
                  <button className="btn btn-primary" disabled={saving} onClick={submitChecklist}>
                    ✅ Submit Checklist
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ON-DEMAND TAB */}
      {activeTab === 'on_demand' && (
        <div>
          <p style={{
            fontSize:13,
            color:'#555',
            marginBottom:16,
            lineHeight:1.8
          }}>
            Start a fresh checklist before performing
            the selected task. Every run is kept as a
            separate permanent record.
          </p>

          {onDemandError && (
            <div style={{
              background:'#FDECEC',
              border:'1px solid #F3C2C2',
              borderRadius:8,
              padding:'10px 14px',
              color:'#8F2E24',
              fontSize:12,
              marginBottom:16
            }}>
              {onDemandError}
            </div>
          )}

          <div style={{
            display:'grid',
            gridTemplateColumns:
              'repeat(auto-fill,minmax(220px,1fr))',
            gap:12
          }}>
            {ON_DEMAND_TYPES
              .filter(
                type =>
                  type.role === user?.role ||
                  isAdmin
              )
              .map(type => (
                <div
                  key={type.key}
                  style={{
                    background:'#fff',
                    border:'1px solid #E8E0D4',
                    borderRadius:10,
                    padding:20,
                    textAlign:'center'
                  }}
                >
                  <div style={{
                    fontSize:32,
                    marginBottom:10
                  }}>
                    {type.label.split(' ')[0]}
                  </div>

                  <div style={{
                    fontWeight:700,
                    fontSize:13,
                    color:'#3B2A21',
                    marginBottom:12
                  }}>
                    {type.label
                      .split(' ')
                      .slice(1)
                      .join(' ')}
                  </div>

                  {isAdmin && (
                    <div style={{
                      fontSize:10,
                      color:'#8A8A8A',
                      marginBottom:10,
                      textTransform:'capitalize'
                    }}>
                      {(
                        ROLE_LABELS[type.role] ||
                        type.role
                      ).replace(/_/g, ' ')}
                    </div>
                  )}

                  <button
                    className="btn btn-primary btn-sm"
                    disabled={
                      Boolean(startingOnDemand)
                    }
                    onClick={() =>
                      startOnDemandChecklist(type)
                    }
                  >
                    {startingOnDemand === type.key
                      ? 'Starting...'
                      : 'Start Checklist'}
                  </button>
                </div>
              ))}
          </div>

          <div style={{
            marginTop:28,
            borderTop:'1px solid #E8E0D4',
            paddingTop:20
          }}>
            <div style={{
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
              gap:12,
              marginBottom:12
            }}>
              <div>
                <div style={{
                  fontWeight:800,
                  color:'#3B2A21',
                  fontSize:14
                }}>
                  On-Demand Runs for {date}
                </div>

                <div style={{
                  color:'#8A8A8A',
                  fontSize:11,
                  marginTop:3
                }}>
                  Open an unfinished run or review a
                  submitted one.
                </div>
              </div>

              <button
                className="btn btn-outline btn-sm"
                disabled={onDemandLoading}
                onClick={loadOnDemand}
              >
                {onDemandLoading
                  ? 'Loading...'
                  : 'Refresh'}
              </button>
            </div>

            {onDemandLoading ? (
              <div className="spinner"/>
            ) : onDemandRuns.length === 0 ? (
              <div className="empty-state">
                <h3>No on-demand runs</h3>
                <p>
                  No on-demand checklist has been
                  started for this date.
                </p>
              </div>
            ) : (
              <div style={{
                display:'grid',
                gap:10
              }}>
                {onDemandRuns.map(record => {
                  const items = record.items || [];
                  const checkboxItems =
                    items.filter(
                      item =>
                        item.item_type ===
                          'checkbox'
                    );

                  const checked =
                    checkboxItems.filter(
                      item => item.checked
                    ).length;

                  return (
                    <button
                      type="button"
                      key={record.id}
                      onClick={() =>
                        openOnDemandChecklist(record)
                      }
                      style={{
                        width:'100%',
                        textAlign:'left',
                        background:'#fff',
                        border:'1px solid #E8E0D4',
                        borderRadius:10,
                        padding:'12px 14px',
                        cursor:'pointer',
                        display:'flex',
                        justifyContent:
                          'space-between',
                        alignItems:'center',
                        gap:12
                      }}
                    >
                      <div>
                        <div style={{
                          fontWeight:700,
                          color:'#3B2A21',
                          fontSize:13
                        }}>
                          {FREQ_LABELS[
                            record.frequency
                          ] || record.frequency}
                          {' '}#{record.seq || 1}
                        </div>

                        <div style={{
                          color:'#8A8A8A',
                          fontSize:11,
                          marginTop:4
                        }}>
                          {checked}/
                          {checkboxItems.length}
                          {' '}checkboxes completed
                        </div>
                      </div>

                      <span style={{
                        fontSize:10,
                        padding:'4px 9px',
                        borderRadius:20,
                        fontWeight:700,
                        background:
                          record.is_complete
                            ? '#E8F5EB'
                            : '#FFF3DC',
                        color:
                          record.is_complete
                            ? '#2E6B3A'
                            : '#8B5E0A'
                      }}>
                        {record.is_complete
                          ? 'Submitted'
                          : 'Continue'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TEMPLATES TAB */}
      {activeTab === 'templates' && isAdmin && (
        <div>
          <div style={{display:'flex',gap:12,marginBottom:20,flexWrap:'wrap'}}>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Role</label>
              <select className="form-control" value={templateRole} onChange={e=>setTemplateRole(e.target.value)}>
                {Object.entries(ROLE_LABELS).map(([k,v])=>(
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{margin:0}}>
              <label className="form-label">Frequency</label>
              <select className="form-control" value={templateFreq} onChange={e=>setTemplateFreq(e.target.value)}>
                {Object.entries(FREQ_LABELS).map(([k,v])=>(
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Add new item */}
          <div style={{background:'#F9F8F5',border:'1px solid #E8E0D4',borderRadius:10,
            padding:16,marginBottom:20}}>
            <div style={{fontWeight:700,fontSize:13,color:'#3B2A21',marginBottom:12}}>
              Add Checklist Item
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr auto',gap:10,alignItems:'end'}}>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Section</label>
                <select className="form-control" value={newItem.section}
                  onChange={e=>setNewItem({...newItem,section:e.target.value})}>
                  {Object.entries(SECTION_LABELS).map(([k,v])=>(
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{margin:0}}>
                <label className="form-label">Type</label>
                <select className="form-control" value={newItem.item_type}
                  onChange={e=>setNewItem({...newItem,item_type:e.target.value})}>
                  <option value="checkbox">Checkbox</option>
                  <option value="text_input">Text Input</option>
                </select>
              </div>
              <div/>
            </div>
            <div style={{marginTop:10,display:'flex',gap:10}}>
              <input className="form-control" style={{flex:1}} value={newItem.item_text}
                onChange={e=>setNewItem({...newItem,item_text:e.target.value})}
                placeholder="Checklist item text..."
                onKeyDown={async e=>{
                  if (e.key==='Enter' && newItem.item_text) {
                    await post('/checklists/templates', {
                      role:templateRole, frequency:templateFreq,
                      ...newItem, sort_order: templates.length+1
                    });
                    setNewItem({...newItem,item_text:''});
                    loadTemplates();
                  }
                }} />
              <button className="btn btn-primary" onClick={async()=>{
                if (!newItem.item_text) return;
                await post('/checklists/templates', {
                  role:templateRole, frequency:templateFreq,
                  ...newItem, sort_order: templates.length+1
                });
                setNewItem({...newItem,item_text:''});
                loadTemplates();
              }}>Add</button>
            </div>
          </div>

          {/* Template items list */}
          {Object.entries(groupBySection(templates.map(t=>({...t,text:t.item_text})))).map(([section,items])=>(
            <div key={section} style={{marginBottom:16,borderRadius:10,overflow:'hidden',
              border:`1px solid ${SECTION_BORDER[section]||'#E8E0D4'}`}}>
              <div style={{background:SECTION_BORDER[section]||'#556B2F',padding:'10px 16px'}}>
                <div style={{fontWeight:700,fontSize:13,color:'#fff'}}>{SECTION_LABELS[section]||section}</div>
              </div>
              <div style={{background:'#fff'}}>
                {items.map((item,idx)=>(
                  <div key={idx} style={{display:'flex',alignItems:'center',gap:10,
                    padding:'10px 16px',borderBottom:'1px solid #F2EFE7'}}>
                    <span style={{fontSize:11,color:'#8A8A8A',background:'#F2EFE7',
                      borderRadius:4,padding:'2px 6px'}}>
                      {item.item_type==='checkbox'?'☐':'✏️'}
                    </span>
                    {editingItem?.id===item.id ? (
                      <input className="form-control" style={{flex:1,fontSize:13}}
                        value={editingItem.item_text}
                        onChange={e=>setEditingItem({...editingItem,item_text:e.target.value})}
                        onBlur={async()=>{
                          await put('/checklists/templates/'+item.id, editingItem);
                          setEditingItem(null);
                          loadTemplates();
                        }} autoFocus />
                    ) : (
                      <span style={{flex:1,fontSize:13,color:'#3B2A21'}}
                        onDoubleClick={()=>setEditingItem({...item})}>
                        {item.item_text}
                      </span>
                    )}
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}}
                      onClick={()=>setEditingItem({...item})}>Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'#8F2E24'}}
                      onClick={async()=>{
                        if (!window.confirm('Remove this item?')) return;
                        await del('/checklists/templates/'+item.id);
                        loadTemplates();
                      }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {templates.length === 0 && (
            <div className="empty-state">
              <p>No template items for {ROLE_LABELS[templateRole]} ({FREQ_LABELS[templateFreq]})</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
